import React, { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronRight } from 'lucide-react';

/**
 * TokenizedSearchBar – barre de recherche à jetons multi-colonnes.
 *
 * Props:
 *  - tokens        : [{ id, column, columnLabel, value, color }]
 *  - onTokensChange: (newTokens) => void
 *  - columns       : [{ key, label, color }]
 *  - placeholder   : string
 */
export function TokenizedSearchBar({ tokens, onTokensChange, columns, placeholder }) {
  const [inputValue, setInputValue] = useState('');
  const [activeColumn, setActiveColumn] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Fermer dropdown si clic extérieur + vider le texte en cours
  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setInputValue('');
        setActiveColumn(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Colonnes filtrées par la saisie (exclure 'general' du dropdown de colonnes)
  const filteredCols = columns.filter(
    (c) =>
      c.key !== 'general' &&
      (c.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        c.key.toLowerCase().includes(inputValue.toLowerCase()))
  );

  const addToken = (column, value) => {
    if (!value.trim()) return;
    onTokensChange([
      ...tokens,
      {
        id: Date.now() + Math.random(),
        column: column.key,
        columnLabel: column.label,
        value: value.trim(),
        color: column.color,
      },
    ]);
    setInputValue('');
    setActiveColumn(null);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const removeToken = (id) => {
    onTokensChange(tokens.filter((t) => t.id !== id));
  };

  const handleKeyDown = (e) => {
    // Backspace sur champ vide → supprimer dernier token
    if (e.key === 'Backspace' && !inputValue && !activeColumn && tokens.length > 0) {
      removeToken(tokens[tokens.length - 1].id);
      return;
    }
    // Escape
    if (e.key === 'Escape') {
      if (activeColumn) {
        setActiveColumn(null);
        setInputValue('');
      } else {
        setShowDropdown(false);
        setInputValue('');
      }
      return;
    }
    // Entrée
    if (e.key === 'Enter') {
      if (activeColumn) {
        if (inputValue.trim()) addToken(activeColumn, inputValue);
      } else if (inputValue.trim()) {
        if (focusedIdx >= 0 && focusedIdx < filteredCols.length) {
          // Sélectionner la colonne focalisée
          setActiveColumn(filteredCols[focusedIdx]);
          setInputValue('');
          setShowDropdown(false);
        } else {
          // Jeton général
          const gen = columns.find((c) => c.key === 'general');
          if (gen) addToken(gen, inputValue);
        }
      }
      return;
    }
    // Navigation clavier dans dropdown
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(i + 1, filteredCols.length - 1));
    }
    if (e.key === 'ArrowUp' && showDropdown) {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(i - 1, -1));
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setFocusedIdx(-1);
    if (!activeColumn) {
      setShowDropdown(val.length > 0);
    }
  };

  const handleFocus = () => {
    if (!activeColumn && inputValue.length > 0) setShowDropdown(true);
    else if (!activeColumn) setShowDropdown(true);
  };

  const handleColumnClick = (col) => {
    setActiveColumn(col);
    setInputValue('');
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleGeneralClick = () => {
    const gen = columns.find((c) => c.key === 'general');
    if (gen && inputValue.trim()) addToken(gen, inputValue);
  };

  const clearAll = (e) => {
    e.preventDefault();
    onTokensChange([]);
    setInputValue('');
    setActiveColumn(null);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const hasContent = tokens.length > 0 || inputValue || activeColumn;

  return (
    <div ref={containerRef} className="relative flex-1 min-w-48" data-testid="tokenized-search-bar">
      {/* Barre principale */}
      <div
        className="flex items-center flex-wrap gap-1.5 px-3 py-2 rounded-lg border min-h-[42px] cursor-text transition-colors"
        style={{ borderColor: 'var(--text-disabled)', background: 'white' }}
        onClick={() => inputRef.current?.focus()}
      >
        <Search size={14} style={{ color: 'var(--text-placeholder)', flexShrink: 0 }} />

        {/* Badge colonne active */}
        {activeColumn && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold flex-shrink-0"
            style={{
              background: activeColumn.color + '20',
              color: activeColumn.color,
              border: `1px solid ${activeColumn.color}50`,
            }}
          >
            {activeColumn.label} :
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveColumn(null);
                setInputValue('');
              }}
              className="hover:opacity-60 ml-0.5"
            >
              <X size={9} />
            </button>
          </span>
        )}

        {/* Jetons existants */}
        {tokens.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium flex-shrink-0"
            style={{
              background: t.color + '18',
              color: t.color,
              border: `1px solid ${t.color}35`,
            }}
            data-testid={`token-${t.column}`}
          >
            {t.column !== 'general' && (
              <span style={{ opacity: 0.65 }}>{t.columnLabel} :</span>
            )}
            <span>{t.value}</span>
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                removeToken(t.id);
              }}
              className="hover:opacity-60 ml-0.5"
              data-testid={`remove-token-${t.column}`}
            >
              <X size={9} />
            </button>
          </span>
        ))}

        {/* Champ de saisie */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={
            activeColumn
              ? `Valeur pour ${activeColumn.label}…`
              : tokens.length === 0
              ? placeholder || 'Rechercher par colonne…'
              : ''
          }
          className="flex-1 min-w-20 text-sm outline-none bg-transparent"
          style={{ color: 'var(--text)', minWidth: '80px' }}
          data-testid="tokenized-search-input"
        />

        {/* Bouton tout effacer */}
        {hasContent && (
          <button
            onMouseDown={clearAll}
            className="p-0.5 rounded hover:bg-gray-100 flex-shrink-0 transition-colors"
            style={{ color: 'var(--text-muted)' }}
            data-testid="clear-all-tokens"
            title="Tout effacer"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Dropdown des suggestions de colonnes */}
      {showDropdown && !activeColumn && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-xl z-50 py-1.5 overflow-hidden"
          style={{ borderColor: 'var(--border)', maxHeight: '280px', overflowY: 'auto' }}
          data-testid="token-column-dropdown"
        >
          {/* Option recherche générale */}
          {inputValue.trim() && (
            <button
              onMouseDown={handleGeneralClick}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
            >
              <Search size={12} style={{ color: '#6B7280', flexShrink: 0 }} />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Recherche générale :</span>
              <span className="font-medium truncate" style={{ color: 'var(--text)' }}>
                "{inputValue}"
              </span>
            </button>
          )}

          {/* Séparateur */}
          {inputValue.trim() && filteredCols.length > 0 && (
            <div className="mx-3 my-1 border-t" style={{ borderColor: 'var(--border)' }} />
          )}

          {/* Label "Filtrer par colonne" */}
          {filteredCols.length > 0 && (
            <div className="px-3 pb-1 pt-0.5">
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-disabled)' }}>
                Filtrer par colonne
              </span>
            </div>
          )}

          {filteredCols.length === 0 && !inputValue.trim() && (
            <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              Tapez pour rechercher une colonne…
            </div>
          )}

          {filteredCols.map((col, idx) => (
            <button
              key={col.key}
              onMouseDown={() => handleColumnClick(col)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors"
              style={{
                background: focusedIdx === idx ? 'var(--bg-subtle)' : 'transparent',
              }}
              onMouseEnter={() => setFocusedIdx(idx)}
              onMouseLeave={() => setFocusedIdx(-1)}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: col.color }}
              />
              <span style={{ color: 'var(--text-secondary)' }}>{col.label}</span>
              <ChevronRight size={11} className="ml-auto" style={{ color: 'var(--text-disabled)' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
